/** Hand-rolled SVG charts: tiny, fast, themeable, and accessible (each has a text summary). */
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { addDays, diffDays, monthDay, weekdayShort, type ISODate } from '../../shared/dates';
import type { TrendPoint } from '../../shared/trend';
import { formatWeight, kgToLb, type WeightUnit } from '../../shared/units';

function useWidth(ref: RefObject<HTMLElement | null>, fallback = 640): number {
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => entry && setWidth(Math.max(240, Math.round(entry.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

function niceTicks(min: number, max: number, count = 4): number[] {
  const span = max - min || 1;
  const raw = span / count;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? raw;
  const start = Math.ceil(min / step) * step;
  const out: number[] = [];
  for (let v = start; v <= max + 1e-9; v += step) out.push(Number(v.toFixed(6)));
  return out;
}

// ——— Weight chart

interface WeightChartProps {
  points: Array<{ date: ISODate; kg: number }>;
  trend: TrendPoint[];
  goalKg: number | null;
  unit: WeightUnit;
  today: ISODate;
  /** kg per week; draws a dashed forecast for the next few weeks. */
  forecastRate?: number | null;
  height?: number;
}

export function WeightChart({ points, trend, goalKg, unit, today, forecastRate, height = 240 }: WeightChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const width = useWidth(ref);
  const [hover, setHover] = useState<ISODate | null>(null);
  const pad = { l: 40, r: 12, t: 12, b: 26 };

  const model = useMemo(() => {
    // Unrounded conversion keeps the trend line smooth.
    const d = (kg: number) => (unit === 'lb' ? kgToLb(kg) : kg);
    const first = [points[0]?.date, trend[0]?.date].filter(Boolean).sort()[0] as ISODate | undefined;
    if (!first) return null;
    const last = trend[trend.length - 1];
    const forecastDays = forecastRate && last ? 28 : 0;
    const end = addDays(today, forecastDays);
    const span = Math.max(1, diffDays(end, first));
    const values = [...points.map((p) => d(p.kg)), ...trend.map((t) => d(t.kg))];
    let fc: Array<{ date: ISODate; v: number; lo: number; hi: number }> = [];
    if (forecastRate && last) {
      const perDay = d(forecastRate) / 7;
      fc = Array.from({ length: forecastDays + 1 }, (_, i) => {
        const v = d(last.kg) + perDay * (diffDays(today, last.date) + i);
        const spread = Math.abs(perDay) * 0.25 * i + 0.15 * i ** 0.5;
        return { date: addDays(today, i), v, lo: v - spread, hi: v + spread };
      });
      values.push(...fc.map((f) => f.lo), ...fc.map((f) => f.hi));
    }
    const goal = goalKg !== null ? d(goalKg) : null;
    let min = Math.min(...values);
    let max = Math.max(...values);
    const showGoal = goal !== null && goal >= min - (max - min) * 0.6 && goal <= max + (max - min) * 0.6;
    if (showGoal && goal !== null) {
      min = Math.min(min, goal);
      max = Math.max(max, goal);
    }
    const padY = Math.max(0.6, (max - min) * 0.12);
    min -= padY;
    max += padY;
    const innerW = width - pad.l - pad.r;
    const innerH = height - pad.t - pad.b;
    const x = (date: ISODate) => pad.l + (diffDays(date, first) / span) * innerW;
    const y = (v: number) => pad.t + (1 - (v - min) / (max - min)) * innerH;
    // Up to five ticks, never two on the same day (a short range has fewer days than ticks).
    const steps = Math.min(4, span);
    const xTicks = [...new Set(Array.from({ length: steps + 1 }, (_, i) => addDays(first, Math.round((span * i) / steps))))];
    return { d, first, end, x, y, min, max, goal, showGoal, fc, xTicks, yTicks: niceTicks(min, max, 4), innerW };
  }, [points, trend, goalKg, unit, today, forecastRate, width, height, pad.l, pad.r, pad.t, pad.b]);

  if (!model) return <div ref={ref} className="empty small">No weigh-ins yet.</div>;
  const { d, x, y, goal, showGoal, fc, xTicks, yTicks } = model;
  const trendPath = trend.map((t, i) => `${i ? 'L' : 'M'}${x(t.date).toFixed(1)},${y(d(t.kg)).toFixed(1)}`).join('');
  const band = fc.length
    ? `M${fc.map((f) => `${x(f.date).toFixed(1)},${y(f.hi).toFixed(1)}`).join('L')}L${[...fc]
        .reverse()
        .map((f) => `${x(f.date).toFixed(1)},${y(f.lo).toFixed(1)}`)
        .join('L')}Z`
    : '';
  const fcPath = fc.map((f, i) => `${i ? 'L' : 'M'}${x(f.date).toFixed(1)},${y(f.v).toFixed(1)}`).join('');

  const hoverPoint = hover ? points.find((p) => p.date === hover) : undefined;
  const hoverTrend = hover ? trend.find((t) => t.date === hover) : undefined;
  const latest = trend[trend.length - 1];
  const summary = latest
    ? `Weight trend ${formatWeight(latest.kg, unit)} on ${monthDay(latest.date)}${goalKg !== null ? `, goal ${formatWeight(goalKg, unit)}` : ''}.`
    : 'Weight chart';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <svg
        className="chart"
        width={width}
        height={height}
        role="img"
        aria-label={summary}
        onPointerMove={(e) => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const px = e.clientX - rect.left;
          let best: ISODate | null = null;
          let bestDist = Infinity;
          for (const t of trend) {
            const dist = Math.abs(x(t.date) - px);
            if (dist < bestDist) {
              bestDist = dist;
              best = t.date;
            }
          }
          setHover(bestDist < 24 ? best : null);
        }}
        onPointerLeave={() => setHover(null)}
      >
        {yTicks.map((v) => (
          <g key={v}>
            <line className="grid" x1={pad.l} x2={width - pad.r} y1={y(v)} y2={y(v)} />
            <text x={pad.l - 8} y={y(v) + 4} textAnchor="end">
              {Number.isInteger(v) ? v : v.toFixed(1)}
            </text>
          </g>
        ))}
        {xTicks.map((t, i) => (
          <text key={t} x={x(t)} y={height - 6} textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}>
            {monthDay(t)}
          </text>
        ))}
        {showGoal && goal !== null && (
          <g>
            <line x1={pad.l} x2={width - pad.r} y1={y(goal)} y2={y(goal)} stroke="var(--fiber)" strokeWidth={1.5} strokeDasharray="5 5" />
            <text x={width - pad.r} y={y(goal) - 6} textAnchor="end" style={{ fill: 'var(--fiber)', fontWeight: 600 }}>
              Goal {Number(goal.toFixed(1))}
            </text>
          </g>
        )}
        {band && <path d={band} fill="var(--ai)" opacity={0.12} />}
        {fcPath && <path d={fcPath} fill="none" stroke="var(--ai)" strokeWidth={2} strokeDasharray="4 5" />}
        {points.map((p) => (
          <circle key={p.date} cx={x(p.date)} cy={y(d(p.kg))} r={hover === p.date ? 5 : 3} fill="var(--faint)" opacity={0.75} />
        ))}
        <path d={trendPath} fill="none" stroke="var(--ink)" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
        {hover && <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={height - pad.b} stroke="var(--line-strong)" />}
        {latest && <circle cx={x(latest.date)} cy={y(d(latest.kg))} r={5} fill="var(--accent)" stroke="var(--ink)" strokeWidth={2} />}
      </svg>
      {hover && (hoverTrend || hoverPoint) && (
        <div className="tooltip" style={{ left: x(hover), top: y(d((hoverPoint ?? hoverTrend)!.kg)) }}>
          <b>{monthDay(hover)}</b>
          {hoverPoint && ` · weighed ${formatWeight(hoverPoint.kg, unit)}`}
          {hoverTrend && ` · trend ${formatWeight(hoverTrend.kg, unit)}`}
        </div>
      )}
    </div>
  );
}

// ——— Daily bars

interface BarChartProps {
  days: Array<{ date: ISODate; amount: number | null }>;
  target: number | null;
  direction: 'min' | 'max' | 'target';
  unit: string;
  height?: number;
  label: string;
  compact?: boolean;
}

export function BarChart({ days, target, direction, unit, height = 200, label, compact }: BarChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const width = useWidth(ref, compact ? 300 : 640);
  const pad = compact ? { l: 4, r: 4, t: 8, b: 16 } : { l: 40, r: 8, t: 12, b: 24 };
  const values = days.map((d) => d.amount ?? 0);
  const max = Math.max(1, ...values, (target ?? 0) * 1.15) * 1.08;
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const bw = innerW / Math.max(1, days.length);
  const y = (v: number) => pad.t + (1 - v / max) * innerH;
  const logged = days.filter((d) => d.amount !== null);
  const avg = logged.length ? logged.reduce((s, d) => s + (d.amount ?? 0), 0) / logged.length : null;
  const summary = `${label}: average ${avg === null ? 'no data' : `${Math.round(avg)} ${unit}`} over ${logged.length} logged days${target ? `, target ${target} ${unit}` : ''}.`;

  return (
    <div ref={ref}>
      <svg className="chart" width={width} height={height} role="img" aria-label={summary}>
        {!compact &&
          niceTicks(0, max, 3).map((v) => (
            <g key={v}>
              <line className="grid" x1={pad.l} x2={width - pad.r} y1={y(v)} y2={y(v)} />
              <text x={pad.l - 8} y={y(v) + 4} textAnchor="end">
                {v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v)}
              </text>
            </g>
          ))}
        {days.map((d, i) => {
          const v = d.amount;
          const over = target !== null && v !== null && (direction === 'max' ? v > target : direction === 'target' ? v > target * 1.1 : false);
          const met = target !== null && v !== null && direction === 'min' && v >= target;
          const h = v === null ? 0 : Math.max(2, innerH - (y(v) - pad.t));
          const x0 = pad.l + i * bw + bw * 0.18;
          return (
            <g key={d.date}>
              {v === null ? (
                <rect x={x0} y={pad.t + innerH - 3} width={bw * 0.64} height={3} rx={1.5} fill="var(--line)" />
              ) : (
                <rect x={x0} y={pad.t + innerH - h} width={bw * 0.64} height={h} rx={Math.min(6, bw * 0.2)} fill={over ? 'var(--warn)' : met ? 'var(--good)' : 'var(--ink)'} opacity={0.88}>
                  <title>{`${monthDay(d.date)}: ${Math.round(v)} ${unit}`}</title>
                </rect>
              )}
              {(!compact || days.length <= 14) && (days.length <= 10 || i % Math.ceil(days.length / 8) === 0) && (
                <text x={x0 + bw * 0.32} y={height - (compact ? 3 : 6)} textAnchor="middle">
                  {days.length <= 10 ? weekdayShort(d.date).slice(0, 1) : monthDay(d.date)}
                </text>
              )}
            </g>
          );
        })}
        {target !== null && target > 0 && (
          <line x1={pad.l} x2={width - pad.r} y1={y(target)} y2={y(target)} stroke="var(--ai)" strokeWidth={1.5} strokeDasharray="5 4" />
        )}
      </svg>
    </div>
  );
}

// ——— Sparkline for Coach cards

export function Sparkline({ values, height = 56 }: { values: number[]; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const width = useWidth(ref, 280);
  if (values.length < 2) return <div ref={ref} />;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const x = (i: number) => 4 + (i / (values.length - 1)) * (width - 8);
  const y = (v: number) => 4 + (1 - (v - min) / (max - min || 1)) * (height - 8);
  const path = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
  return (
    <div ref={ref}>
      <svg width={width} height={height} aria-hidden="true">
        <path d={path} fill="none" stroke="var(--ink)" strokeWidth={2} strokeLinejoin="round" />
        <circle cx={x(values.length - 1)} cy={y(values[values.length - 1]!)} r={4} fill="var(--accent)" stroke="var(--ink)" strokeWidth={1.5} />
      </svg>
    </div>
  );
}
